// @flow
// force flow to type check this file - https://github.com/zapier/zapier/pull/31127

import React from 'react'; /* eslint-disable-line no-unused-vars */
import { noop } from 'lodash';

import Icon from '@zapier/common-components/src/components/Icon';
import { Colors } from '@zapier/theme';
import { css, jsx } from '@emotion/core';

/** @jsx jsx */

const SIZE = 20;

const wrapperBaseCss = css({
  alignItems: 'center',
  borderColor: Colors.adamantium,
  borderRadius: 3,
  borderStyle: 'solid',
  borderWidth: 1,
  color: Colors.white,
  display: 'inline-flex',
  flex: 'none', // Never flex if a flex child
  height: SIZE,
  justifyContent: 'center',
  position: 'relative',
  width: SIZE,
});

const radioButtonCss = css({
  borderRadius: '100%',
});

const checkedCss = css({
  backgroundColor: Colors.heroine,
  border: `1px solid ${Colors.heroine}`,
});

const disabledCss = css({
  opacity: 0.25,
  cursor: 'default',
});

const inputCss = css({
  height: '100%',
  left: 0,
  margin: 0,
  padding: 0,
  position: 'absolute',
  top: 0,
  opacity: 0,
  width: '100%',
});

const iconName = props => {
  if (props.indeterminate) {
    return 'indeterminate';
  }

  if (props.checked) {
    return props.type === 'checkbox' ? 'check-alt' : 'disc';
  }

  return 'disc';
};

type ChangeDescription = {
  name: string,
  value: string,
  checked: boolean,
};

type Props = {
  checked?: boolean,
  indeterminate?: boolean,
  name?: string,
  id?: string,
  disabled?: boolean,
  value?: string,
  onFocus?: (event: SyntheticInputEvent<HTMLInputElement>) => void,
  onBlur?: (event: SyntheticInputEvent<HTMLInputElement>) => void,
  onChange: (changed: ChangeDescription) => void,
  type: 'checkbox' | 'radio',
  'aria-label'?: string,
  'aria-labelledby'?: string,
  'aria-required'?: string,
};

/**
 * Renders a checkbox or a radio button.
 */
const BooleanInput = (props: Props) => {
  const wrapperCss = css(
    wrapperBaseCss,
    props.type === 'radio' && radioButtonCss,
    (props.checked || props.indeterminate) && checkedCss,
    props.disabled && disabledCss
  );

  const iconScale = props.type === 'checkbox' ? 0.5 : 0.3;

  const iconCss = css({
    height: SIZE * iconScale,
    width: SIZE * iconScale,
  });

  const handleChange = (e: SyntheticInputEvent<HTMLInputElement>) => {
    // Use `checked` from the DOM node to ensure accuracy.
    // Using `this.props.checked` can be trickier because
    // we need to negate it and there may be a race condition
    // between the event and when the DOM is updated.
    const { checked, name, value } = e.target;

    props.onChange({
      checked,
      name,
      value,
    });
  };

  return (
    <span
      aria-checked={props.indeterminate ? 'mixed' : Boolean(props.checked)}
      aria-label={props['aria-label']}
      aria-labelledby={props['aria-labelledby']}
      aria-required={props['aria-required']}
      css={wrapperCss}
      role={props.type === 'radio' ? 'radio' : 'checkbox'}
    >
      <input
        aria-hidden={true}
        aria-label={props['aria-label']}
        checked={props.checked}
        css={inputCss}
        disabled={props.disabled}
        id={props.id}
        name={props.name}
        onBlur={props.onBlur}
        onChange={handleChange}
        onFocus={props.onFocus}
        type={props.type}
        value={props.value}
      />
      <Icon css={iconCss} name={iconName(props)} size={12} />
    </span>
  );
};

BooleanInput.defaultProps = {
  disabled: false,
  indeterminate: false,
  onChange: noop,
  type: 'checkbox',
};

export default BooleanInput;
